import { useState } from 'react';
import { changeKind } from '@/domain';
import { Button } from '@/ui/primitives/Button';
import { shortened } from './shortened';
import styles from './BeforeAfter.module.css';

/** One field of a thing as a change left it: what it read before and what it reads now. */
export interface BeforeAfterEntry {
  /** The field's name, as a person reads it. */
  label: string;
  /** What it read before. Null when there was nothing there: the field was filled in. */
  before: string | null;
  /** What it reads now. Null when nothing is there any more: the field was emptied. */
  after: string | null;
  /** A value this reader may not see. The row says that it changed, and no more. */
  hidden?: boolean;
}

/** The words the rows need, handed in by the screen so they stay with its own. */
export interface BeforeAfterWords {
  /** Read out before the old value, for somebody who cannot see the line through it. */
  was: string;
  /** Read out before the new value, for somebody who cannot see its tint. */
  now: string;
  /** In place of both values on a row whose values are hidden. */
  hidden: string;
  showAll: string;
  showLess: string;
}

export interface BeforeAfterProps {
  entries: readonly BeforeAfterEntry[];
  words: BeforeAfterWords;
}

function Row({ entry, words }: { entry: BeforeAfterEntry; words: BeforeAfterWords }) {
  const [open, setOpen] = useState(false);

  // Hidden, or nothing on either side to show: the row still says the field changed.
  const hidden = entry.hidden === true || (entry.before === null && entry.after === null);
  const before = hidden ? null : entry.before;
  const after = hidden ? null : entry.after;

  const shortBefore = before === null ? null : shortened(before);
  const shortAfter = after === null ? null : shortened(after);
  const long = shortBefore !== null || shortAfter !== null;

  return (
    <div className={styles.row} data-change={hidden ? 'hidden' : changeKind(entry)}>
      <dt className={styles.label}>{entry.label}</dt>
      <dd className={styles.values}>
        {before !== null && (
          <del className={styles.old}>
            <span className="visually-hidden">{words.was} </span>
            {open ? before : (shortBefore ?? before)}
          </del>
        )}
        {after !== null && (
          <ins className={styles.new}>
            <span className="visually-hidden">{words.now} </span>
            {open ? after : (shortAfter ?? after)}
          </ins>
        )}
        {hidden && <span className={styles.hidden}>{words.hidden}</span>}
        {long && (
          <Button
            variant="text"
            className={styles.more}
            aria-expanded={open}
            onClick={() => setOpen((shown) => !shown)}
          >
            {open ? words.showLess : words.showAll}
          </Button>
        )}
      </dd>
    </div>
  );
}

/**
 * What changed, field by field: before and after. The request of
 * 5 October 2026 (third), section 3: only the changed fields, the old value
 * struck through and the new one highlighted.
 *
 * One row per field. The old value is a `del` and the new one an `ins`, which
 * is what they are, and each is led by a word only a screen reader says, "was"
 * and "now", so the difference never rests on a line through the text or on a
 * colour. The highlight is the green of a settled status and never red, which
 * means urgent and nothing else.
 *
 * The old value sits above the new one at every width, not beside it: a pair
 * side by side needs a sign between the two to be read in the right order, and
 * instructions run to thousands of characters, which no column on a phone
 * holds. Stacked, each wraps in the full width of whatever it is put in.
 *
 * A field filled in for the first time shows the new value alone, one emptied
 * shows the old value alone, and one the reader may not see (a doctor's or a
 * patient's name, without the permission for it) says only that it changed.
 * A long value is cut at about six lines with Show all under the pair, which
 * opens both sides of it together.
 */
export function BeforeAfter({ entries, words }: BeforeAfterProps) {
  return (
    <dl className={styles.list}>
      {entries.map((entry, index) => (
        // By position: one change may list the same field twice, a bridge added and a bar taken off.
        <Row key={index} entry={entry} words={words} />
      ))}
    </dl>
  );
}
